import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ArrowRight, MessageSquare, Users } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Seo } from '@/components/seo/Seo';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  conversationMethodIds,
  describeMyConversation,
  type ConversationLens,
  type MyConversationRow,
} from '@/lib/my-conversations';

const PAGE_SIZE = 24;
type Filter = 'all' | 'chat' | 'board';

const formatDate = (value: string | null): string => {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
};

const loadPage = async (userId: string, page: number) => {
  const { data, error } = await supabase
    .from('conversations')
    .select('id, thinking_method_id, openai_conversation_id, updated_at, created_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .order('id', { ascending: false })
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  if (error) throw error;
  const rows = (data ?? []) as MyConversationRow[];
  const ids = conversationMethodIds(rows);
  const lenses = new Map<string, ConversationLens>();
  // Batch lookups keep long boards within URL limits and avoid per-conversation requests.
  for (let offset = 0; offset < ids.length; offset += 50) {
    const { data: lensData, error: lensError } = await supabase
      .from('thinking_methods')
      .select('id, name, author')
      .in('id', ids.slice(offset, offset + 50));
    if (lensError) throw lensError;
    for (const lens of lensData ?? []) lenses.set(lens.id, lens);
  }

  return {
    items: rows.map((row) => describeMyConversation(row, lenses)),
    nextPage: rows.length === PAGE_SIZE ? page + 1 : undefined,
  };
};

const MyConversations = () => {
  const { user, loading: authLoading, isAnonymous } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  const userId = !isAnonymous ? user?.id : undefined;
  const query = useInfiniteQuery({
    queryKey: ['my-conversations', userId],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => loadPage(userId!, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !authLoading && !!userId,
  });
  const allItems = query.data?.pages.flatMap((page) => page.items) ?? [];
  const visibleItems = filter === 'all' ? allItems : allItems.filter((item) => item.kind === filter);
  const needsAccount = !authLoading && !userId;

  return (
    <div className="flex min-h-screen flex-col bg-[hsl(var(--brand-accent-warm-bg))]/30 text-foreground">
      <Seo title="Applyt: My Conversations" description="Reopen your Lens chats and Expert Boards." />
      <Header />
      <main className="w-full flex-1">
        <section className="border-b border-border/70 bg-card px-4 py-10 sm:px-6 sm:py-14">
          <div className="mx-auto max-w-6xl">
            <h1 className="text-3xl font-bold sm:text-4xl" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Conversations</h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">Your Lens chats and Expert Boards, with the most recently updated first.</p>
          </div>
        </section>
        <section className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10" aria-label="Your conversations">
          {authLoading ? (
            <p role="status" className="text-muted-foreground">Loading conversations…</p>
          ) : needsAccount ? (
            <div className="rounded-xl border bg-card p-8 text-center">
              <h2 className="text-xl font-semibold">Sign in to see your conversations</h2>
              <p className="mt-2 text-muted-foreground">Your chats and boards will appear here.</p>
              <Button asChild className="mt-5"><Link to="/auth">Sign in</Link></Button>
            </div>
          ) : (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filter conversations">
                  {([['all', 'All'], ['chat', 'Chats'], ['board', 'Boards']] as const).map(([value, label]) => (
                    <Button key={value} type="button" size="sm" variant={filter === value ? 'default' : 'outline'} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</Button>
                  ))}
                </div>
                <Link to="/expert-boards" className="text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:underline">My Boards</Link>
              </div>
              {query.isPending ? (
                <p role="status" className="text-muted-foreground">Loading conversations…</p>
              ) : query.isError && allItems.length === 0 ? (
                <div role="alert" className="rounded-xl border bg-card p-8 text-center">
                  <p className="font-semibold">Couldn’t load your conversations.</p>
                  <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>Try again</Button>
                </div>
              ) : (
                <>
                  {visibleItems.length === 0 && (
                    <div className="rounded-xl border border-dashed bg-card p-8 text-center">
                      <MessageSquare className="mx-auto mb-3 h-10 w-10 text-muted-foreground" aria-hidden="true" />
                      <p className="font-semibold">{allItems.length === 0 ? 'No conversations yet' : `No ${filter === 'board' ? 'boards' : 'chats'} in the conversations loaded so far`}</p>
                      <p className="mt-2 text-sm text-muted-foreground">{query.hasNextPage ? 'Load more to see older conversations.' : 'Start a Lens chat or create an Expert Board to see it here.'}</p>
                    </div>
                  )}
                  {visibleItems.length > 0 && (
                    <ul className="grid gap-3">
                      {visibleItems.map((item) => (
                        <li key={item.id}>
                          <Link to={item.path} className="group flex min-w-0 items-center gap-4 rounded-xl border border-border/70 bg-card p-4 shadow-sm transition-colors hover:border-primary/40 hover:bg-card/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-5">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary" aria-hidden="true">{item.kind === 'board' ? <Users className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}</span>
                            <span className="min-w-0 flex-1">
                              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{item.kind === 'board' ? 'Expert Board' : 'Lens chat'}</span>
                              <span className="block break-words text-base font-semibold">{item.title}</span>
                              <span className="mt-1 block break-words text-sm text-muted-foreground">{item.experts.length ? item.experts.join(' · ') : 'Expert unavailable'}</span>
                              <span className="mt-2 block text-xs text-muted-foreground">Updated {formatDate(item.updatedAt)}</span>
                            </span>
                            <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-primary" aria-hidden="true" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {query.isFetchNextPageError && <p role="alert" className="mt-5 text-sm text-destructive">Couldn’t load older conversations. Please try again.</p>}
                  {query.hasNextPage && (
                    <div className="mt-8 text-center">
                      <Button variant="outline" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>{query.isFetchingNextPage ? 'Loading…' : 'Load more'}</Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MyConversations;
